import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { resolveStoreName } from '@/lib/display';
import { isMarketplaceTheme } from '@/lib/marketplace';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import MarketplaceCategoriesTag from '@/components/feature/template-tags/MarketplaceCategoriesTag';
import MarketplaceProductsTag from '@/components/feature/template-tags/MarketplaceProductsTag';
import ProductsTag from '@/components/feature/template-tags/ProductsTag';
import ServicesTag from '@/components/feature/template-tags/ServicesTag';
import { fetchMarketplaceCategories, type MarketplaceCategory } from '@/lib/marketplaceCatalog';

type Tab = 'annonces' | 'produits' | 'services';

interface CategoryWithChain extends MarketplaceCategory {
  chain: { id: number; nom: string }[];
}

export default function CategoriesPage() {
  const { tenant, theme } = useTenant();
  const [activeTab, setActiveTab] = useState<Tab>('annonces');
  const [query, setQuery] = useState('');
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);

  useEffect(() => {
    document.title = 'Catégories';
  }, []);

  const storeName = resolveStoreName(tenant, theme);
  const marketplace = isMarketplaceTheme(theme);
  const merchantId = tenant?.id ? Number(tenant.id) : null;

  useEffect(() => {
    if (!merchantId) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    fetchMarketplaceCategories(merchantId)
      .then((list) => { if (!cancelled) setCategories(list); })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, [merchantId]);

  const byId = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  // Chaîne complète (Catégorie › Sous-catégorie › Sous-sous-catégorie).
  const chainOf = (id: number): { id: number; nom: string }[] => {
    const out: { id: number; nom: string }[] = [];
    let cur: number | null = id;
    let guard = 0;
    while (cur && guard < 6) {
      guard += 1;
      const c = byId.get(cur);
      if (!c) break;
      out.unshift({ id: c.id, nom: c.nom });
      cur = c.parent_id ?? null;
    }
    return out;
  };

  // Recherche par mot-clé : on cherche sur TOUS les niveaux (catégorie,
  // sous-catégorie, sous-sous-catégorie) et on affiche le résultat avec sa
  // chaîne complète — un mot-clé révèle donc directement les sous-sous-catégories.
  const q = query.trim().toLowerCase();
  const matched: CategoryWithChain[] = useMemo(() => {
    if (!q) return [];
    return categories
      .filter((c) => (c.nom || '').toLowerCase().includes(q))
      .map((c) => ({ ...c, chain: chainOf(c.id) }))
      .sort((a, b) => b.chain.length - a.chain.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, q]);

  const tabs: Array<{ key: Tab; label: string; icon: string }> = [
    { key: 'annonces', label: 'Annonces', icon: 'ri-megaphone-line' },
    { key: 'produits', label: 'Produits', icon: 'ri-shopping-bag-line' },
    { key: 'services', label: 'Services', icon: 'ri-service-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] font-semibold uppercase tracking-wider mb-3">
              <i className="ri-apps-2-line"></i>
              Explorer
            </span>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              Catégories
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              Recherchez une catégorie directement — sous-catégories et sous-sous-catégories comprises.
            </p>
          </div>

          <div className="max-w-xl mx-auto">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une catégorie (ex : immobilier, électronique…)"
                className="w-full h-12 pl-11 pr-11 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                  aria-label="Effacer la recherche"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Catégories */}
      <section className="pb-8">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          {q ? (
            <>
              <h2 className="text-sm font-bold uppercase tracking-wider text-foreground-600 mb-4 flex items-center gap-2">
                <i className="ri-search-line"></i>
                {matched.length} catégorie{matched.length > 1 ? 's' : ''} correspondant à « {query.trim()} »
              </h2>
              {matched.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                    <i className="ri-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">Aucune catégorie ne correspond à votre recherche.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {matched.map((cat) => {
                    const img = resolveMediaUrl(cat.image_couverture);
                    return (
                      <Link
                        key={cat.id}
                        to={`/marketplace/categorie/${cat.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-background-300/60 hover:bg-background-100/50 transition-colors cursor-pointer no-underline group"
                      >
                        <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:bg-primary-200/70 transition-colors">
                          {img ? (
                            <img src={img} alt={cat.nom} className="w-full h-full object-cover" />
                          ) : (
                            <i className={`${cat.icon || 'ri-folder-line'} text-lg text-primary-600`}></i>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 flex-wrap">
                            {cat.chain.map((c, idx) => (
                              <span key={c.id} className="flex items-center gap-1">
                                {idx > 0 && <i className="ri-arrow-right-s-line text-foreground-300 text-[10px]"></i>}
                                <span className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${idx === cat.chain.length - 1 ? 'bg-secondary-100 text-secondary-900 font-semibold' : 'bg-background-100 text-foreground-500'}`}>
                                  {c.nom}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                        <i className="ri-arrow-right-s-line text-foreground-300 flex-shrink-0"></i>
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <h2 className="text-sm font-bold uppercase tracking-wider text-foreground-600 mb-4">
                Toutes les catégories
              </h2>
              <MarketplaceCategoriesTag />
            </>
          )}
        </div>
      </section>

      {/* Navigation par type */}
      <section className="pb-16">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-6">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  activeTab === t.key
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                <i className={t.icon}></i>
                {t.label}
              </button>
            ))}
          </div>

          <div>
            {activeTab === 'annonces' && (marketplace ? <MarketplaceProductsTag /> : <ProductsTag />)}
            {activeTab === 'produits' && <ProductsTag />}
            {activeTab === 'services' && <ServicesTag />}
          </div>
        </div>
      </section>
    </div>
  );
}